import { HugeiconsIcon } from "@hugeicons/react";
import { Store01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { ChartCard } from "@/components/dashboard/chart-card";
import { CategoryBars } from "@/components/dashboard/charts";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { brandLabelCol, regionLabelCol, spendCol, revenueCol, roasCol, ordersCol, exactReachCol, exactErCol } from "@/components/dashboard/columns";
import { useReference } from "@/lib/use-reference";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FadeIn } from "@/components/ui/motion";
import { useBreakdown } from "@/lib/use-metrics";
import { useMetricMoney } from "@/lib/use-money";
import { useExactReach } from "@/lib/use-reach";
import type { BreakdownRow } from "@/lib/api/metrics";

export function Brands() {
  const m = useMetricMoney();
  const { brands } = useReference();
  const byBrand = useBreakdown("brand");
  const byRegion = useBreakdown("region");
  const reach = useExactReach();
  const loading = byRegion.isPending || byBrand.isPending;

  const brandColumns: Column<BreakdownRow>[] = [
    brandLabelCol("Merk", brands), spendCol(m.fmt), revenueCol(m.fmt), roasCol(), ordersCol(), exactReachCol(reach, "brand"),
    exactErCol(reach, "brand"),
  ];

  const regionColumns: Column<BreakdownRow>[] = [
    regionLabelCol("Regio"),
    spendCol(m.fmt), revenueCol(m.fmt), roasCol(), exactReachCol(reach, "region"), ordersCol(),
  ];

  const revenueBars = (byBrand.data ?? []).map((r) => ({ label: r.label, revenue: r.revenue }));
  const reachBars = (byBrand.data ?? []).map((r) => ({ label: r.label, reach: reach.get("brand", r.key)?.reach ?? 0 }));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Merken & regio's"
        icon={Store01Icon}
        title="Merken & regio's"
        description="Vergelijk Prado Official, Rond en Prado USA — en EU tegenover US."
        tabs={<GlobalFilterBar />}
      />

      {loading ? (
        <div className="space-y-6">
          <Skeleton className="h-40 rounded-2xl" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-96 rounded-2xl" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      ) : (
        <>
          <FadeIn delay={0}>
            <BreakdownTable title="Per regio" description="Kerncijfers per markt (EU/US/CA) over de gekozen periode." rows={byRegion.data ?? []} columns={regionColumns} rowKey={(r) => r.key} exportName="Prado Pulse — Per regio" />
          </FadeIn>

          <FadeIn delay={0.05}>
            <div className="grid gap-4 lg:grid-cols-2">
              <ChartCard title="Omzet per merk" bodyClassName="h-80">
                <CategoryBars data={revenueBars} dataKey="revenue" kind="currency" moneyFmt={m.fmt} />
              </ChartCard>
              <ChartCard title="Bereik per merk" bodyClassName="h-80">
                <CategoryBars data={reachBars} dataKey="reach" />
              </ChartCard>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <BreakdownTable title="Per merk" description="Kerncijfers per merk over de gekozen periode." rows={byBrand.data ?? []} columns={brandColumns} rowKey={(r) => r.key} exportName="Prado Pulse — Per merk" />
          </FadeIn>
        </>
      )}
    </div>
  );
}
